/**
 * Section: Gombok és linkek — serviceURLs. Template-aware slots, each emitted
 * as a serviceURLs.<key> merge patch. All [TEMPLATE]; plain text inputs.
 *
 * The travel (boardingPass) canvas offers all six Apple service URL slots with
 * the airline wording. The GENERAL template's canvas (style 'generic' —
 * altalanos → generic) offers only the two that make sense on an insurance
 * pass: management ("Pass kezelése") and a neutrally-worded contact link
 * ("Kapcsolat" — still the contactAirlineURL slot underneath, so the design
 * shape stays one schema). Free-form buttons stay available on both templates
 * via the sibling FeaturedActionsEditor.
 *
 * The server's per-key findings (path: 'serviceURLs.<key>', e.g. the
 * https-only scheme ERROR) render inline under their input — red for error,
 * amber for warn (same severity styling as the sibling BarcodeEditor/
 * ColorsEditor sections).
 */
import type {
  DeepPartial,
  PassDesign,
  PassDesignError,
  PassDesignServiceURLs,
} from '@insurwiz/wallet-core';

import { useT, type MessageKey } from '../../i18n';
import { HelpText } from '../designer/HelpHint';

export interface ServiceURLsEditorProps {
  design: PassDesign;
  errors: PassDesignError[];
  onChange: (patch: DeepPartial<PassDesign>) => void;
}

type UrlRow = { key: keyof PassDesignServiceURLs; labelKey: MessageKey };

const TRAVEL_URL_ROWS: ReadonlyArray<UrlRow> = [
  { key: 'managementURL', labelKey: 'serviceUrls.management' },
  { key: 'purchaseLoungeAccessURL', labelKey: 'serviceUrls.purchaseLoungeAccess' },
  { key: 'purchaseSeatUpgradeURL', labelKey: 'serviceUrls.purchaseSeatUpgrade' },
  { key: 'purchaseWiFiURL', labelKey: 'serviceUrls.purchaseWiFi' },
  { key: 'checkBaggageURL', labelKey: 'serviceUrls.checkBaggage' },
  { key: 'contactAirlineURL', labelKey: 'serviceUrls.contactAirline' },
];

/** GENERAL template: management + neutral contact only (no airline slots). */
const GENERIC_URL_ROWS: ReadonlyArray<UrlRow> = [
  { key: 'managementURL', labelKey: 'serviceUrls.managePass' },
  { key: 'contactAirlineURL', labelKey: 'serviceUrls.contact' },
];

export function ServiceURLsEditor({
  design,
  errors,
  onChange,
}: ServiceURLsEditorProps): JSX.Element {
  const t = useT();
  const serviceURLs = design.serviceURLs ?? {};
  const generic = design.style === 'generic';
  const rows = generic ? GENERIC_URL_ROWS : TRAVEL_URL_ROWS;
  return (
    <section className="card p-4 space-y-3" aria-label={t('serviceUrls.title')}>
      <h3 className="text-sm font-semibold text-slate-800">{t('serviceUrls.title')}</h3>
      <HelpText>{t(generic ? 'serviceUrls.help.generic' : 'serviceUrls.help.travel')}</HelpText>
      {rows.map(({ key, labelKey }) => {
        const label = t(labelKey);
        const notes = errors.filter((e) => e.path === `serviceURLs.${key}`);
        return (
          <label key={key} className="block text-xs text-slate-600">
            {label}
            <input
              aria-label={label}
              type="url"
              className="input mt-1 w-full"
              value={serviceURLs[key] ?? ''}
              // Empty input DELETES the key via mergePassDesign's null
              // sentinel — writing '' would fail the https-only scheme check
              // forever (a cleared field must mean "no link", not "bad link").
              onChange={(e) =>
                onChange({ serviceURLs: { [key]: e.target.value === '' ? null : e.target.value } })
              }
            />
            {notes.map((e, i) => (
              <p
                key={`${e.path}-${String(i)}`}
                className={`mt-1 text-xs ${e.severity === 'error' ? 'text-red-600' : 'text-amber-700'}`}
              >
                {e.message}
              </p>
            ))}
          </label>
        );
      })}
    </section>
  );
}
